import { useMemo, useState } from 'react'
import Modal from './Modal.jsx'
import { DateRangeField } from './DatePicker.jsx'
import { Switch } from './SettingsLayout.jsx'
import { addDays, daysInRange, formatDate, formatShort, isoWeekday, weekOf, weekdayName } from '../lib/dates.js'

// The server's bound on one copy (repos/assignments.js MAX_COPY_DATES).
const MAX_DAYS = 62

/**
 * Copying one day's jobs onto other days — "Marc is on Edson and Jasper again all week".
 *
 * Pasting puts one job on one day. This puts all of a person's jobs, or everybody's, on as
 * many days as asked, through the server's copy-day, in one request and one undo-able batch
 * of audit events.
 *
 * The range picks the stretch and the chips under it pick the days, because the stretch is
 * almost never every day in it: the week's working days start ticked, a Saturday can be added
 * by hand, and a day that is already sorted can be left out without splitting the range.
 */
export default function CopyDayModal({
  date,
  person = null, // { id, name } — null opens it on the whole day
  personJobs = [], // labels of that person's jobs on the day
  everyoneCount = 0, // how many jobs the whole day holds
  weekStart = 1,
  workDays = [1, 2, 3, 4, 5],
  onSubmit,
  onClose,
}) {
  const [scope, setScope] = useState(person ? 'person' : 'everyone')

  const presets = useMemo(() => {
    const week = weekOf(date, weekStart)
    const next = weekOf(addDays(date, 7), weekStart)
    const out = []
    if (addDays(date, 1) <= week.to) out.push({ label: 'Rest of this week', from: addDays(date, 1), to: week.to })
    out.push({ label: 'Next week', from: next.from, to: next.to })
    return out
  }, [date, weekStart])

  const [from, setFrom] = useState(presets[0].from)
  const [to, setTo] = useState(presets[0].to)
  // Only the days somebody has clicked; every other day follows the working week.
  const [overrides, setOverrides] = useState(() => new Map())
  const [skipHolidays, setSkipHolidays] = useState(true)
  const [busy, setBusy] = useState(false)

  const span = daysInRange(from, to)
  const tooLong = span > MAX_DAYS
  const days = useMemo(() => {
    if (!from || !to || to < from || tooLong) return []
    return Array.from({ length: span }, (_, i) => addDays(from, i))
  }, [from, to, span, tooLong])

  const working = useMemo(() => new Set(workDays.map(Number)), [workDays])
  const isOn = (d) => d !== date && (overrides.has(d) ? overrides.get(d) : working.has(isoWeekday(d)))
  const picked = days.filter(isOn)

  const toggle = (d) => setOverrides((prev) => new Map(prev).set(d, !isOn(d)))

  const jobs = scope === 'person' ? personJobs.length : everyoneCount
  const nothing = jobs === 0

  async function submit(e) {
    e.preventDefault()
    if (!picked.length || nothing || busy) return
    setBusy(true)
    try {
      await onSubmit({
        toDates: picked,
        staffUserIds: scope === 'person' ? [person.id] : undefined,
        skipHolidays,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={`Copy ${formatDate(date)}`} onClose={onClose} width={500}>
      <form onSubmit={submit}>
        {person && (
          <div className="segmented copy-day-scope" role="group" aria-label="Whose jobs">
            <button type="button" className={scope === 'person' ? 'selected' : ''} onClick={() => setScope('person')}>
              {person.name} · {personJobs.length} {personJobs.length === 1 ? 'job' : 'jobs'}
            </button>
            <button type="button" className={scope === 'everyone' ? 'selected' : ''} onClick={() => setScope('everyone')}>
              Everyone · {everyoneCount} {everyoneCount === 1 ? 'job' : 'jobs'}
            </button>
          </div>
        )}
        {scope === 'person' && personJobs.length > 0 && (
          <p className="copy-day-what">{personJobs.join(', ')}</p>
        )}

        <DateRangeField
          label="Onto"
          from={from}
          to={to}
          onChange={(f, t) => { setFrom(f); setTo(t) }}
          weekStart={weekStart}
          today={date}
          presets={presets}
        />

        {tooLong ? (
          <div className="notice">At most {MAX_DAYS} days at a time. Pick a shorter stretch.</div>
        ) : (
          <div className="copy-day-chips" role="group" aria-label="Days to copy onto">
            {days.map((d) => (
              <button
                key={d}
                type="button"
                className={`copy-day-chip ${isOn(d) ? 'on' : ''}`}
                aria-pressed={isOn(d)}
                disabled={d === date}
                title={d === date ? 'The day being copied' : undefined}
                onClick={() => toggle(d)}
              >
                <span className="copy-day-chip-wd">{weekdayName(d)}</span>
                {formatShort(d)}
              </button>
            ))}
          </div>
        )}

        <div className="asset-toggle-row">
          <div className="asset-toggle-text">
            <span className="asset-toggle-label">Skip statutory holidays</span>
            <span className="hint">The days the board marks STAT</span>
          </div>
          <Switch checked={skipHolidays} onChange={setSkipHolidays} label="Skip statutory holidays" />
        </div>

        <p className="hint copy-day-rules">
          A job already in the same column is kept, and nobody is booked onto a day they are off
          all day.
        </p>

        <div className="modal-actions">
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={!picked.length || nothing || busy}>
            {busy ? 'Copying…' : `Copy onto ${picked.length} ${picked.length === 1 ? 'day' : 'days'}`}
          </button>
        </div>
      </form>
    </Modal>
  )
}
